/**
 * Components API Client
 * Handles all component-related API requests
 */

import type {
  Component,
  CreateComponentDTO,
  UpdateComponentDTO
} from '@fmeastudio/shared'
import { API_BASE_URL } from './config'
import { throwIfNotOk } from '../utils/apiErrors'
import { withActor } from '../utils/interlockActor'

const BASE_URL = `${API_BASE_URL}/components`

export interface ComponentFilters {
  projectId?: string
  componentType?: string
  parentId?: string | null
  verificationStatus?: string
  search?: string
}

/**
 * Get all components with optional filtering
 */
export async function getComponents(filters?: ComponentFilters): Promise<Component[]> {
  const params = new URLSearchParams()

  if (filters?.projectId) params.append('projectId', filters.projectId)
  if (filters?.componentType) params.append('type', filters.componentType)
  if (filters?.parentId !== undefined) {
    params.append('parentId', filters.parentId || '')
  }
  if (filters?.verificationStatus) params.append('verificationStatus', filters.verificationStatus)
  if (filters?.search) params.append('search', filters.search)

  const url = params.toString() ? `${BASE_URL}?${params}` : BASE_URL
  const response = await fetch(url)

  if (!response.ok) {
    throw new Error('Failed to fetch components')
  }

  const data = await response.json()
  return data.data || []
}

/**
 * Get a single component by ID.
 *
 * Optional `projectId` scopes the lookup server-side (Ontology O4, PR#148
 * review): the backend 404s when the component belongs to another project, so
 * project-scoped pages can't render a foreign project's data.
 */
export async function getComponent(id: string, projectId?: string): Promise<Component> {
  const url = projectId
    ? `${BASE_URL}/${id}?projectId=${encodeURIComponent(projectId)}`
    : `${BASE_URL}/${id}`
  const response = await fetch(url)

  if (!response.ok) {
    throw new Error('Failed to fetch component')
  }

  const data = await response.json()
  return data.data
}

/**
 * Create a new component
 */
export async function createComponent(data: CreateComponentDTO & { projectId: string }): Promise<Component> {
  const { projectId, ...componentData } = data

  const response = await fetch(withActor(`${BASE_URL}?projectId=${projectId}`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(componentData),
  })

  if (!response.ok) {
    throw new Error('Failed to create component')
  }

  const result = await response.json()
  return result.data
}

/**
 * Update an existing component
 */
export async function updateComponent(
  id: string,
  data: UpdateComponentDTO
): Promise<Component> {
  const response = await fetch(withActor(`${BASE_URL}/${id}`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })

  // Optimistic-lock frontend wiring fix: prefer the server's own message
  // (e.g. the 409 VERSION_CONFLICT "modified by someone else — reload and
  // retry" copy from ComponentsService.update) over a generic status-code
  // string, same idiom as hooks/useRequirements.ts.
  await throwIfNotOk(
    response,
    response.status === 404 ? 'Component not found (404)' : `Failed to update component (${response.status})`
  )

  const result = await response.json()
  return result.data
}

/**
 * Delete a component (soft delete)
 */
export async function deleteComponent(id: string): Promise<void> {
  const response = await fetch(withActor(`${BASE_URL}/${id}`), {
    method: 'DELETE',
  })

  if (!response.ok) {
    throw new Error('Failed to delete component')
  }
}

/** What deleting a component would cascade — used by the delete-confirm modal. */
export interface ComponentDeletionImpact {
  componentName: string
  childComponentCount: number
  interfaces: Array<{ id: string; name: string }>
  failureModeCount: number
}

/**
 * Preview the cascade of deleting a component (dependent interfaces, descendant
 * components, owned failure modes) WITHOUT deleting. Powers the honest
 * delete-confirmation modal so the user sees the exact interface count/names
 * before the destructive action.
 */
export async function getComponentDeletionImpact(id: string): Promise<ComponentDeletionImpact> {
  const response = await fetch(`${BASE_URL}/${id}/deletion-impact`)

  if (!response.ok) {
    throw new Error('Failed to load deletion impact')
  }

  const data = await response.json()
  return data.data
}

/**
 * Get component hierarchy (tree structure)
 */
export async function getComponentHierarchy(rootId?: string, projectId?: string): Promise<Component[]> {
  let url = rootId
    ? `${BASE_URL}/hierarchy/${rootId}`
    : `${BASE_URL}/hierarchy`

  if (projectId) {
    url += `?projectId=${projectId}`
  }

  const response = await fetch(url)

  if (!response.ok) {
    throw new Error('Failed to fetch component hierarchy')
  }

  const data = await response.json()
  return data.data || []
}

/**
 * Get children of a specific component
 */
export async function getComponentChildren(parentId: string): Promise<Component[]> {
  const response = await fetch(`${BASE_URL}/${parentId}/children`)

  if (!response.ok) {
    throw new Error('Failed to fetch component children')
  }

  const data = await response.json()
  return data.data || []
}

/**
 * Move a component to a new parent
 */
export async function moveComponent(
  id: string,
  newParentId: string | null
): Promise<Component> {
  const response = await fetch(withActor(`${BASE_URL}/${id}/move`), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ newParentId }),
  })

  if (!response.ok) {
    throw new Error('Failed to move component')
  }

  const result = await response.json()
  return result.data
}

/**
 * Upload thumbnail for a component
 */
export async function uploadThumbnail(
  componentId: string,
  file: File
): Promise<{ thumbnailId: string; thumbnailUrl: string }> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await fetch(withActor(`${BASE_URL}/${componentId}/thumbnail`), {
    method: 'POST',
    body: formData,
  })

  if (!response.ok) {
    throw new Error('Failed to upload thumbnail')
  }

  const result = await response.json()
  return result.data
}

/**
 * Delete thumbnail from a component
 */
export async function deleteThumbnail(componentId: string): Promise<void> {
  const response = await fetch(withActor(`${BASE_URL}/${componentId}/thumbnail`), {
    method: 'DELETE',
  })

  if (!response.ok) {
    throw new Error('Failed to delete thumbnail')
  }
}

/**
 * Update component diagram position (SoW-297)
 */
export async function updateComponentPosition(
  id: string,
  position: { diagramX: number; diagramY: number; diagramWidth?: number; diagramHeight?: number }
): Promise<Component> {
  const response = await fetch(withActor(`${BASE_URL}/${id}/position`), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(position),
  })

  if (!response.ok) {
    throw new Error('Failed to update component position')
  }

  const result = await response.json()
  return result.data
}

/**
 * Update component container diagram position
 * Used when viewing a component as a container in drilled-down view
 */
export async function updateComponentContainerPosition(
  id: string,
  position: {
    containerDiagramX: number
    containerDiagramY: number
    containerDiagramWidth?: number
    containerDiagramHeight?: number
  }
): Promise<Component> {
  const response = await fetch(withActor(`${BASE_URL}/${id}/container-position`), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(position),
  })

  if (!response.ok) {
    throw new Error('Failed to update component container position')
  }

  const result = await response.json()
  return result.data
}

/**
 * Update component diagram collapsed state (SoW-297-003)
 */
export async function updateComponentCollapsed(
  id: string,
  isCollapsed: boolean
): Promise<Component> {
  const response = await fetch(withActor(`${BASE_URL}/${id}/collapsed`), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ isCollapsed }),
  })

  if (!response.ok) {
    throw new Error('Failed to update component collapsed state')
  }

  const result = await response.json()
  return result.data
}
